import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://linkedin-backend.gentleforest-c3f8de6c.southeastasia.azurecontainerapps.io/api',
  timeout: 600000, // 10 minutes timeout (increased for processing all connections without limits)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let authToken = localStorage.getItem('authToken');

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      removeAuthToken();
      window.location.href = '/login';
      toast.error('Session expired. Please log in again.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }
    return Promise.reject(error);
  }
);

// Auth token management
export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

export const removeAuthToken = () => {
  authToken = null;
  localStorage.removeItem('authToken');
};

export const getAuthToken = () => {
  return authToken || localStorage.getItem('authToken');
};

// API endpoints
export const authAPI = {
  googleLogin: (token) => api.post('/auth/google', { token }),
  getUserInfo: () => api.get('/auth/user'),
  verifyToken: () => api.post('/auth/verify'),
  refreshToken: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
};

export const uploadAPI = {
  uploadConnections: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/upload/connections', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for upload
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
  },
  getUploadStatus: () => api.get('/upload/status'),
  getSampleCSV: () => api.get('/upload/sample'),
  generateEmbeddings: () => api.post('/upload/generate-embeddings', {}, { timeout: 300000 }), // 5 minutes timeout
  deleteConnections: (deleteFiles = false) =>
    api.delete(`/upload/delete?delete_files=${deleteFiles}`),
};

export const searchAPI = {
  searchConnections: (query, topK = 5, includeExplanations = false) =>
    api.post('/search', {
      query,
      top_k: topK,
      include_explanations: includeExplanations,
    }),
  getSearchSuggestions: () => api.get('/search/suggestions'),
  findSimilarConnections: (connectionId) => 
    api.get(`/search/similar/${connectionId}`),
  getSearchStats: () => api.get('/search/stats'),
};

export const historyAPI = {
  getQueryHistory: (page = 1, perPage = 20) =>
    api.get(`/history/queries?page=${page}&per_page=${perPage}`),
  deleteQuery: (queryId) => api.delete(`/history/queries/${queryId}`),
  clearQueryHistory: () => api.delete('/history/queries'),
  getPopularQueries: (limit = 10) => 
    api.get(`/history/popular?limit=${limit}`),
  getRecentQueries: (days = 7, limit = 10) =>
    api.get(`/history/recent?days=${days}&limit=${limit}`),
  getQueryStats: () => api.get('/history/stats'),
  exportQueryHistory: () => api.get('/history/export'),
};

// Utility functions
export const handleAPIError = (error, defaultMessage = 'An error occurred') => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  } else if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

export const isAuthError = (error) => {
  return error.response?.status === 401;
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;